/**
 * SQL Editor Tab component with Monaco Editor and results display.
 * Inspired by DBeaver's SQL Editor.
 */

import React, { useState, useRef, useEffect } from 'react';
import Editor from '@monaco-editor/react';
import {
  Box,
  Button,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Switch,
  FormControlLabel,
  Tooltip,
  IconButton,
  useTheme,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import SaveIcon from '@mui/icons-material/Save';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { SqlTab, Connection, QueryResult } from '../types';
import { queryApi, connectionApi, schemaApi } from '../services/api';
import { addQueryToHistory } from '../services/storage';
import ResultsGrid from './ResultsGrid';
import AiAssistant from './AiAssistant';

interface SqlEditorTabProps {
  tab: SqlTab;
  connections: Connection[];
  selectedConnectionId: string | null;
  autoCommit: boolean;
  onUpdateTab: (tab: SqlTab) => void;
  onSelectConnection: (connectionId: string) => void;
  onToggleAutoCommit: () => void;
}

const SqlEditorTab: React.FC<SqlEditorTabProps> = ({
  tab,
  connections,
  selectedConnectionId,
  autoCommit,
  onUpdateTab,
  onSelectConnection,
  onToggleAutoCommit,
}) => {
  const theme = useTheme();
  const editorRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [executing, setExecuting] = useState(false);
  const [aiAssistantOpen, setAiAssistantOpen] = useState(false);
  const [schemas, setSchemas] = useState<string[]>([]);
  const [loadingSchemas, setLoadingSchemas] = useState(false);
  const [editorHeight, setEditorHeight] = useState<number>(() => {
    // Load saved preference or default to 60%
    const saved = localStorage.getItem('sqlEditorHeight');
    return saved ? parseFloat(saved) : 60;
  });
  const [isDragging, setIsDragging] = useState(false);
  const isDarkMode = theme.palette.mode === 'dark';

  useEffect(() => {
    // Update tab's connection if selected connection changes
    if (selectedConnectionId && selectedConnectionId !== tab.connectionId) {
      onUpdateTab({
        ...tab,
        connectionId: selectedConnectionId,
        schema: undefined, // Reset schema when connection changes
      });
      setSchemas([]);
    }
  }, [selectedConnectionId]);

  // Load schemas when tab's connection changes
  useEffect(() => {
    if (tab.connectionId) {
      console.log('Loading schemas for connection:', tab.connectionId);
      loadSchemas(tab.connectionId);
    } else {
      setSchemas([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab.connectionId]);

  const loadSchemas = async (connectionId: string) => {
    if (!connectionId) {
      setSchemas([]);
      return;
    }
    
    setLoadingSchemas(true);
    try {
      const schemasList = await schemaApi.getSchemas(connectionId);
      console.log('Loaded schemas:', schemasList);
      setSchemas(schemasList || []);
    } catch (err: any) {
      console.error('Failed to load schemas:', err);
      console.error('Error details:', err.response?.data || err.message);
      setSchemas([]);
    } finally {
      setLoadingSchemas(false);
    }
  };

  const handleSchemaChange = (schema: string) => {
    onUpdateTab({
      ...tab,
      schema: schema || undefined,
    });
  };

  // Handle resizing of editor and results pane
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const containerHeight = containerRect.height;
      const mouseY = e.clientY - containerRect.top;
      
      // Calculate percentage, with min/max constraints
      let newHeight = (mouseY / containerHeight) * 100;
      newHeight = Math.max(20, Math.min(80, newHeight)); // Between 20% and 80%
      
      setEditorHeight(newHeight);
      localStorage.setItem('sqlEditorHeight', newHeight.toString());
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  const handleEditorDidMount = (editor: any, monaco: any) => {
    editorRef.current = editor;
    
    // Add keyboard shortcut for execute (Ctrl+Enter)
    editor.addCommand(
      monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter,
      () => {
        handleExecute();
      }
    );
  };

  const handleEditorChange = (value: string | undefined) => {
    onUpdateTab({
      ...tab,
      sql: value || '',
      isDirty: true,
    });
  };

  const handleExecute = async () => {
    // Check if there's a selection - if so, execute only that
    if (editorRef.current) {
      const editor = editorRef.current;
      const selection = editor.getSelection();
      
      if (selection && !selection.isEmpty()) {
        const selectedText = editor.getModel().getValueInRange(selection);
        if (selectedText && selectedText.trim()) {
          // There's a valid selection - execute only that
          await handleExecuteSelected();
          return;
        }
      }
    }

    // No selection - execute entire query
    if (!tab.connectionId) {
      alert('Please select a connection first');
      return;
    }

    if (!tab.sql.trim()) {
      alert('Please enter a SQL query');
      return;
    }

    setExecuting(true);

    try {
      // Ensure connection exists in backend before executing
      const connection = connections.find(c => c.id === tab.connectionId);
      if (connection) {
        try {
          // Try to get the connection from backend
          await connectionApi.getById(tab.connectionId);
        } catch (err) {
          // Connection doesn't exist in backend, re-save it
          if (connection.id) {
            await connectionApi.update(connection.id, connection);
          } else {
            await connectionApi.create(connection);
          }
        }
      }

      const result = await queryApi.execute({
        connectionId: tab.connectionId,
        sql: tab.sql,
        autoCommit,
        schema: tab.schema,
      });

      onUpdateTab({
        ...tab,
        results: result,
        isExecuting: false,
      });

      // Add to history
      addQueryToHistory({
        id: `history_${Date.now()}`,
        timestamp: Date.now(),
        sql: tab.sql,
        executionTime: result.executionTime,
        status: result.error ? 'error' : 'success',
        connectionId: tab.connectionId,
        error: result.error,
      });
    } catch (err: any) {
      const errorResult: QueryResult = {
        columns: [],
        rows: [],
        executionTime: 0,
        error: err.message || 'Query execution failed',
      };

      onUpdateTab({
        ...tab,
        results: errorResult,
        isExecuting: false,
      });
    } finally {
      setExecuting(false);
    }
  };

  const handleExecuteSelected = async () => {
    if (!editorRef.current) {
      handleExecute();
      return;
    }

    const editor = editorRef.current;
    const model = editor.getModel();
    const selection = editor.getSelection();

    // Check if there's a valid selection
    if (!selection || selection.isEmpty()) {
      // No selection - execute entire query
      handleExecute();
      return;
    }

    // Get the selected text
    const selectedText = model.getValueInRange(selection);

    if (!selectedText || !selectedText.trim()) {
      // Empty selection - execute entire query
      handleExecute();
      return;
    }

    // Execute only the selected text
    if (!tab.connectionId) {
      alert('Please select a connection first');
      return;
    }

    setExecuting(true);

    try {
      // Ensure connection exists in backend before executing
      const connection = connections.find(c => c.id === tab.connectionId);
      if (connection) {
        try {
          // Try to get the connection from backend
          await connectionApi.getById(tab.connectionId);
        } catch (err) {
          // Connection doesn't exist in backend, re-save it
          if (connection.id) {
            await connectionApi.update(connection.id, connection);
          } else {
            await connectionApi.create(connection);
          }
        }
      }

      const result = await queryApi.execute({
        connectionId: tab.connectionId,
        sql: selectedText.trim(), // Use selected text, trimmed
        autoCommit,
        schema: tab.schema,
      });

      onUpdateTab({
        ...tab,
        results: result,
        isExecuting: false,
      });

      // Add to history with selected text
      addQueryToHistory({
        id: `history_${Date.now()}`,
        timestamp: Date.now(),
        sql: selectedText.trim(),
        executionTime: result.executionTime,
        status: result.error ? 'error' : 'success',
        connectionId: tab.connectionId,
        error: result.error,
      });
    } catch (err: any) {
      const errorResult: QueryResult = {
        columns: [],
        rows: [],
        executionTime: 0,
        error: err.message || 'Query execution failed',
      };

      onUpdateTab({
        ...tab,
        results: errorResult,
        isExecuting: false,
      });
    } finally {
      setExecuting(false);
    }
  };

  const handleStop = () => {
    setExecuting(false);
    // Note: Actual query cancellation would require backend support
  };

  const handleInsertSql = (sql: string) => {
    if (editorRef.current) {
      const editor = editorRef.current;
      const model = editor.getModel();
      const position = editor.getPosition();
      
      // Insert SQL at cursor position
      const edit = {
        range: {
          startLineNumber: position.lineNumber,
          startColumn: position.column,
          endLineNumber: position.lineNumber,
          endColumn: position.column,
        },
        text: sql,
      };
      
      editor.executeEdits('insert-sql', [edit]);
      
      // Update tab state
      onUpdateTab({
        ...tab,
        sql: model.getValue(),
        isDirty: true,
      });
    } else {
      // Fallback: just update the tab SQL
      onUpdateTab({
        ...tab,
        sql: tab.sql + '\n' + sql,
        isDirty: true,
      });
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Toolbar */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', p: 1 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Connection</InputLabel>
            <Select
              value={tab.connectionId || ''}
              label="Connection"
              onChange={(e) => onSelectConnection(e.target.value)}
            >
              {connections.length === 0 ? (
                <MenuItem value="">No connections</MenuItem>
              ) : (
                connections.map((conn) => (
                  <MenuItem key={conn.id} value={conn.id}>
                    {conn.name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 180 }} disabled={!tab.connectionId || loadingSchemas}>
            <InputLabel>Schema</InputLabel>
            <Select
              value={tab.schema || ''}
              label="Schema"
              onChange={(e) => handleSchemaChange(e.target.value)}
              onOpen={() => {
                // Reload schemas when dropdown opens
                if (tab.connectionId && !loadingSchemas) {
                  loadSchemas(tab.connectionId);
                }
              }}
            >
              <MenuItem value="">(Default)</MenuItem>
              {loadingSchemas ? (
                <MenuItem disabled>Loading schemas...</MenuItem>
              ) : schemas.length === 0 ? (
                <MenuItem disabled>No schemas found</MenuItem>
              ) : (
                schemas.map((schema) => (
                  <MenuItem key={schema} value={schema}>
                    {schema}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          <Tooltip title="Execute Query (Ctrl+Enter)">
            <Button
              variant="contained"
              size="small"
              startIcon={executing ? <CircularProgress size={16} /> : <PlayArrowIcon />}
              onClick={handleExecute}
              disabled={executing || !tab.connectionId}
            >
              Execute
            </Button>
          </Tooltip>

          <Tooltip title="Execute Selected">
            <Button
              variant="outlined"
              size="small"
              onClick={handleExecuteSelected}
              disabled={executing || !tab.connectionId}
            >
              Execute Selected
            </Button>
          </Tooltip>

          {executing && (
            <Button
              variant="outlined"
              size="small"
              color="error"
              startIcon={<StopIcon />}
              onClick={handleStop}
            >
              Stop
            </Button>
          )}

          <FormControlLabel
            control={
              <Switch
                checked={autoCommit}
                onChange={onToggleAutoCommit}
                size="small"
              />
            }
            label="Auto-commit"
          />

          {tab.isDirty && (
            <IconButton size="small" title="Save SQL">
              <SaveIcon fontSize="small" />
            </IconButton>
          )}

          <Tooltip title="AI Assistant">
            <Button
              variant="outlined"
              size="small"
              startIcon={<AutoAwesomeIcon />}
              onClick={() => setAiAssistantOpen(true)}
              disabled={!tab.connectionId}
            >
              AI Assistant
            </Button>
          </Tooltip>
        </Stack>
      </Box>

      {/* Resizable Editor and Results Container */}
      <Box ref={containerRef} sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', position: 'relative' }}>
        {/* Editor */}
        <Box sx={{ height: `${editorHeight}%`, minHeight: '100px', overflow: 'hidden' }}>
          <Editor
            height="100%"
            defaultLanguage="sql"
            value={tab.sql}
            onChange={handleEditorChange}
            onMount={handleEditorDidMount}
            theme={isDarkMode ? "vs-dark" : "vs"}
            options={{
              minimap: { enabled: true },
              fontSize: 14,
              lineNumbers: 'on',
              automaticLayout: true,
              scrollBeyondLastLine: false,
              wordWrap: 'on',
              tabSize: 2,
            }}
          />
        </Box>

        {/* Resizer Divider */}
        <Box
          onMouseDown={handleMouseDown}
          sx={{
            height: '6px',
            cursor: 'row-resize',
            backgroundColor: isDragging ? 'primary.main' : 'divider',
            borderTop: 1,
            borderBottom: 1,
            borderColor: 'divider',
            transition: isDragging ? 'none' : 'background-color 0.2s',
            '&:hover': {
              backgroundColor: 'primary.light',
            },
            position: 'relative',
            zIndex: 10,
            userSelect: 'none',
          }}
        >
          {/* Visual indicator for the divider */}
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '40px',
              height: '3px',
              backgroundColor: 'action.active',
              borderRadius: '2px',
            }}
          />
        </Box>

        {/* Results Grid */}
        <Box sx={{ height: `${100 - editorHeight}%`, minHeight: '100px', overflow: 'auto' }}>
          <ResultsGrid result={tab.results || null} loading={executing} />
        </Box>
      </Box>

      {/* AI Assistant Dialog */}
      <AiAssistant
        open={aiAssistantOpen}
        onClose={() => setAiAssistantOpen(false)}
        connectionId={tab.connectionId || null}
        currentSql={tab.sql}
        onInsertSql={handleInsertSql}
      />
    </Box>
  );
};

export default SqlEditorTab;

